import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ethers } from 'ethers'
import { useSelector } from 'react-redux'
import Big from 'big.js'
import styled, { css } from 'styled-components'
import * as Sentry from '@sentry/react'
import { Memecoin } from '../../../types/reducerTypes'
import { Box } from '../../../shared_components/UI/Box'
import useWeb3 from '../../../hooks/useWeb3'
import { amountFromWei, amountToWei } from '../../../utils/amount'
import {
  selectBalanceTokenSale,
  selectEthUsdPrice,
} from '../../../modules/web3/selectors'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import useTokenContractValues from '../useTokenContractValues'
import TokenProgress from '../TokenProgress'
import TradeAmountInput, {
  TradeAmountInputLabelEth,
  TradeAmountInputLabelText,
  TradeAmountInputLabelToken,
} from '../../../shared_components/Library/TradeAmount/TradeAmountInput'
import TradeAmountPresets from '../../../shared_components/Library/TradeAmount/TradeAmountPresets'
import InfluencerTradesSign from './InfluencerTradesSign'
import {
  formatNumberToFirstDecimalDigit,
  formatNumberWithDecimalSubscript,
} from './helpers'
import useIsLoggedIn from '../../../hooks/useIsLoggedIn'
import {
  H2,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import {
  ThemedAnchor,
  ThemedButton,
} from '../../../shared_components/ThemedButton'
import { Link } from 'react-router-dom'
import useEthBalance from '../../../hooks/useWeb3/useEthBalance'
import ConnectWalletButton from '../../../shared_components/Library/Buttons/ConnectWalletButton'
import SwitchToBaseButton from '../../../shared_components/Library/Buttons/SwitchToBaseButton'
import useNetworkId from '../../../hooks/useNetworkId'
import { useDynamicContext } from '@dynamic-labs/sdk-react-core'
import { UseDynamicContext } from '@dynamic-labs/sdk-react-core/src/lib/context/DynamicContext/useDynamicContext'
import { TokenLogo } from '.'
import { Chip as ChipButton } from '../../../shared_components/Library/Chip'
import { PoolInput } from '../../PoolDetails/parts'

const PCT_FEE = BigInt(1)

function getAmountWithFee(_amount: number) {
  if (!_amount) return 0
  const feeAmount = _amount * 0.01
  const withFee = _amount + feeAmount
  const decimalPlaces = withFee.toString().split('.')[1]?.length
  return decimalPlaces > 14 ? Number(withFee.toFixed(14)) : withFee
}

const Invest = ({
  memecoin,
  tokenPriceInEth,
}: {
  memecoin: Memecoin
  tokenPriceInEth: number
}) => {
  const {
    ethRaisedDecimal,
    totalEthToRaiseDecimal,
    poolContractInstance,
    erc20ContractInstance,
    poolContractEthersInstance,
    erc20ContractEthersInstance,
    isSaleComplete,
    percentageProgress,
    decimals,
    userTokenBalance,
    refetch,
  } = useTokenContractValues({ memecoin })

  return (
    <Container>
      <Trade
        contractInstance={poolContractInstance}
        poolContractEthersInstance={poolContractEthersInstance}
        erc20ContractInstance={erc20ContractInstance}
        isSaleComplete={isSaleComplete}
        decimals={decimals}
        userTokenBalance={userTokenBalance}
        refetch={refetch}
        tokenLogo={memecoin?.media?.logo?.url ?? '/meme-icon.png'}
        tokenSymbol={memecoin?.tokenSymbol ?? 'MEME'}
        poolAddress={memecoin?.poolAddress}
        liquidityPoolAddress={memecoin?.liquidityPoolAddress}
        tokenAddress={memecoin?.tokenAddress}
        erc20ContractEthersInstance={erc20ContractEthersInstance}
        tokenPriceInEth={tokenPriceInEth}
      />
    </Container>
  )
}

const PRESET_ETH_AMOUNTS = [0.001, 0.01, 0.05, 0.1, 0.25]
const PRESET_TOKEN_AMOUNTS = [1000, 10000, 50000, 100000, Infinity]
const SLIPPAGE_TOLERANCES = [5, 10, 15, 20, 30, 0]
const defaultSlippageTolerance = 1

function Trade({
  contractInstance,
  erc20ContractInstance,
  isSaleComplete,
  decimals,
  refetch,
  tokenLogo,
  tokenSymbol,
  poolAddress,
  liquidityPoolAddress,
  userTokenBalance,
  poolContractEthersInstance,
  erc20ContractEthersInstance,
  tokenAddress,
  tokenPriceInEth,
}: {
  contractInstance: ethers.Contract | null
  erc20ContractInstance: ethers.Contract | null
  isSaleComplete: boolean
  decimals: number
  refetch: () => Promise<void>
  tokenLogo: string
  tokenSymbol: string
  poolAddress: string
  liquidityPoolAddress?: string
  userTokenBalance: string
  poolContractEthersInstance: ethers.Contract | null
  erc20ContractEthersInstance: ethers.Contract | null
  tokenAddress: string
  tokenPriceInEth: number
}) {
  const [isBuying, setIsBuying] = useState(true)
  const [isInEth, setIsInEth] = useState(true)
  const [slippageTolerance, setSlippageTolerance] = useState(
    defaultSlippageTolerance,
  )
  const [amount, setAmount] = useState<number>()

  const buyInputRef = useRef(null)
  const sellInputRef = useRef(null)
  const isInitialRender = useRef(true)

  const { address: walletAddress, isLoggedIn } = useIsLoggedIn()
  const { wrongNetwork } = useNetworkId()

  const balanceInSaleToken = useSelector(selectBalanceTokenSale)

  const [minTokensOut, setMinTokensOut] = useState(0)
  const [minEthOut, setMinEthOut] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const ethPriceInUsd = useSelector(selectEthUsdPrice)

  const minTokensOutInWeiString = BigInt(
    minTokensOut * 10 ** decimals,
  ).toString()

  const { initWeb3, isMetaMaskMobile, provider } = useWeb3()
  const { primaryWallet } = useDynamicContext()
  const { ethBalanceInEthFormatted, getEthBalance } = useEthBalance()

  const getEthAmount = useCallback(
    async (_amount: number) => {
      if (!_amount) return

      const ethBalance = await getEthBalance()
      const amountWithFee = getAmountWithFee(_amount)
      console.log({ amountWithFee, _amount })

      const amountInWei = ethers.parseUnits(amountWithFee.toString(), 18)
      console.log({ amountInWei })
      const hasEnoughEth = !!ethBalance && ethBalance > amountInWei
      console.log({ ethBalance, amountInWei, hasEnoughEth })
      return {
        ethAmount: amountInWei,
        hasEnoughEth,
      }
    },
    [getEthBalance],
  )

  const getTokensAmount = useCallback(
    async (_amount: number, _decimals: number, isSell?: boolean) => {
      if (!_amount) return

      console.log({ _amount, _decimals, isSell })

      const amountInWei = amountToWei(
        // we don't want fee on sales - this is taken from the contract directly in the ETH returned
        isSell ? _amount : getAmountWithFee(_amount),
        _decimals,
      ).toString()

      console.log({ amountInWei, userTokenBalance })
      return {
        tokensAmount: amountInWei,
        hasEnoughTokens: new Big(userTokenBalance).gte(amountInWei),
      }
    },
    [userTokenBalance],
  )

  const fetchQuoteInTokensForEthAmount = useCallback(async () => {
    try {
      if (!contractInstance) return { error: 'No contract instance' }
      if (!amount) return { error: 'No amount' }

      const { ethAmount, hasEnoughEth } = await getEthAmount(amount)
      console.log({ ethAmount, hasEnoughEth })
      if (!hasEnoughEth)
        return {
          error: 'Not enough ETH balance',
        }

      try {
        const quote =
          await contractInstance.getTokensOutForEthDeposit(ethAmount)
        return { quote }
      } catch (error) {
        if (
          error.message.includes(
            'Purchase would exceed max supply allowed per trade',
          )
        ) {
          toast.error('ETH amount is too high')
        }
        console.error('Error on getTokensOutForEthDeposit', error)
        Sentry.captureException(error)
        return { error: error.message }
      }
    } catch (error) {
      console.error('Error on fetchQuoteInTokensForEthAmount', error)
      Sentry.captureException(error)
    }
  }, [getEthAmount, amount, contractInstance])

  const fetchQuoteInEthForTokensAmount = useCallback(async () => {
    try {
      if (!contractInstance) return { error: 'No contract instance' }
      if (!amount) return { error: 'No amount' }

      const { tokensAmount, hasEnoughTokens } = await getTokensAmount(
        amount,
        decimals,
        true, // is a sell in here!
      )
      if (!hasEnoughTokens) {
        return {
          error: `Not enough ${tokenSymbol} balance`,
        }
      }

      const quote =
        await contractInstance.getEthOutForTokenDeposit(tokensAmount)

      return { quote }
    } catch (error) {
      console.error('Error on fetchQuoteInEthForTokensAmount', error)
      Sentry.captureException(error)
    }
  }, [contractInstance, amount, getTokensAmount, decimals, tokenSymbol])

  const handleBuy = useCallback(async () => {
    try {
      const { ethAmount, hasEnoughEth } = await getEthAmount(amount)
      if (!hasEnoughEth) {
        toast.error('Not enough ETH balance')
        return
      }

      console.log({ ethAmount, amount, minTokensOutInWeiString })

      const toSend = BigInt(ethAmount)

      let gasEstimate
      try {
        console.log('Calling purchase gas estimate', {
          toSend,
          minTokensOut,
          minTokensOutInWeiString,
          value: addFeeToValue(toSend),
        })
        // This doesnt work on tenderly
        gasEstimate = await poolContractEthersInstance['purchase'].estimateGas(
          toSend,
          minTokensOutInWeiString,
          {
            value: addFeeToValue(toSend),
          },
        )
      } catch (error) {
        console.error('Error estimating gas', error)
        Sentry.captureException(error)
        toast.error('Error estimating gas')
        return
      }

      let tradeTx
      try {
        console.info('Calling purchase', {
          toSend,
          toSendWithFee: addFeeToValue(toSend),
          minTokensOutInWeiString,
          gasEstimate,
        })

        // This doesnt work on tenderly
        //const feeData = await signer.provider.getFeeData()

        tradeTx = await poolContractEthersInstance.purchase(
          toSend,
          BigInt(minTokensOutInWeiString),
          {
            value: addFeeToValue(toSend),
            //gasLimit: 420690000,
            // This doesn't work on tenderly
            gasLimit: gasEstimate,
          },
        )
      } catch (error) {
        console.error('Error completing purchase', error)
        Sentry.captureException(error)
        if (error.message.includes('from should be same as current address')) {
          toast.error(
            'Connected wallet mismatch. Please try refreshing the page.',
          )
        } else {
          toast.error('Error completing purchase')
        }
        return
      }
      await tradeTx.wait() // important as this will wait for the tx to be mined
      if (!tradeTx) throw new Error('No trade tx')
      await addTokenToWallet(
        tokenAddress,
        tokenSymbol,
        decimals,
        provider,
        primaryWallet,
      )
    } catch (error) {
      console.error('Error on handleBuy', error)
      Sentry.captureException(error)
      toast.error('Error with purchase')
    }
  }, [getEthAmount, amount, tokenAddress, tokenSymbol, decimals, provider, primaryWallet, minTokensOut, minTokensOutInWeiString, poolContractEthersInstance])

  const handleSell = useCallback(async () => {
    try {
      if (!erc20ContractEthersInstance)
        throw new Error('No ERC20 contract instance')
      const { tokensAmount, hasEnoughTokens } = await getTokensAmount(
        amount,
        decimals,
        true, // selling doesn't have a fee
      )

      if (!hasEnoughTokens) {
        toast.error(`Not enough "${tokenSymbol}" balance`)
        return
      }

      const toSendInWei = tokensAmount

      console.info('Calling allowance')
      const currentApprovalAmount = await erc20ContractEthersInstance[
        'allowance'
      ](walletAddress, poolAddress)

      if (new Big(currentApprovalAmount).lt(new Big(toSendInWei))) {
        console.info(`Calling approve. toSendInWei: ${toSendInWei}`)
        const gas = await erc20ContractEthersInstance['approve'].estimateGas(
          poolAddress,
          toSendInWei,
        )
        console.info(`Calling approve post gas estimation. gas: ${gas}`)
        const gasTx = await erc20ContractEthersInstance['approve'](
          poolAddress,
          toSendInWei,
          {
            gasLimit: gas,
            // from: walletAddress,
          },
        )
        await gasTx.wait()
      }

      console.info(
        `Calling sell gas estimate. toSendInWei: ${toSendInWei}, minTokensOutInWeiString: ${minTokensOutInWeiString}`,
      )
      const gasEstimate = await poolContractEthersInstance['sell'].estimateGas(
        toSendInWei,
        minTokensOutInWeiString,
      )

      console.info(
        `Calling sell toSendInWei: ${toSendInWei} minTokensOutInWeiString: ${minTokensOutInWeiString} gasEstimate: ${gasEstimate}`,
      )
      const tradeTx = await poolContractEthersInstance['sell'](
        toSendInWei,
        minTokensOutInWeiString,
        {
          gasLimit: gasEstimate,
        },
      )
      await tradeTx.wait() // important as this will wait for the tx to be mined
      if (!tradeTx) throw new Error('No trade tx')
    } catch (error) {
      console.error('Error on handleSell', error)
      Sentry.captureException(error)
      if (error.message.includes('from should be same as current address')) {
        toast.error(
          'Connected wallet mismatch. Please try refreshing the page.',
        )
      } else {
        toast.error('Error with sale')
      }
    }
  }, [
    getTokensAmount,
    amount,
    decimals,
    walletAddress,
    poolAddress,
    poolContractEthersInstance,
    erc20ContractEthersInstance,
    minTokensOutInWeiString,
    tokenSymbol,
  ])

  const handleTrade = useCallback(async () => {
    try {
      if (isSaleComplete) {
        toast.error('Sale is complete - buy on DEX instead')
        return
      }
      if (!isLoggedIn) {
        initWeb3()
        return
      }
      setIsSubmitting(true)

      if (!contractInstance) return
      if (!amount) throw new Error('Invalid amount')
      if (amount < 0) throw new Error('Invalid negative amount')
      if (isBuying) await handleBuy()
      else await handleSell()
      await refetch()
    } catch (error) {
      console.error('Error on handleTrade', error)
      Sentry.captureException(error)
      toast.error('Error with trade')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    isSaleComplete,
    isLoggedIn,
    contractInstance,
    amount,
    isBuying,
    handleBuy,
    handleSell,
    refetch,
    initWeb3,
  ])

  const handleEstimate = useCallback(async () => {
    try {
      if (!slippageTolerance) {
        setMinTokensOut(1)
        return
      }
      if (isInEth) {
        const { quote: tokensOutForEth } =
          await fetchQuoteInTokensForEthAmount()
        if (!tokensOutForEth) {
          setMinTokensOut(0)
          return
        }
        const minTokensOut = new Big(tokensOutForEth)
          .times(1 - slippageTolerance / 100)
          .times(10 ** -decimals)
          .toNumber()
        setMinTokensOut(Math.floor(minTokensOut))
      } else {
        const { quote: ethOutForTokens } =
          await fetchQuoteInEthForTokensAmount()
        if (!ethOutForTokens) {
          setMinEthOut(0)
          return
        }
        const minEthOut = new Big(ethOutForTokens)
          .times(1 - slippageTolerance / 100)
          .times(10 ** -18)
          .toNumber()
        setMinEthOut(minEthOut)
      }
    } catch (error) {
      console.error('Error on handleEstimate', error)
      Sentry.captureException(error)
    }
  }, [
    slippageTolerance,
    isInEth,
    fetchQuoteInTokensForEthAmount,
    decimals,
    fetchQuoteInEthForTokensAmount,
  ])

  useEffect(() => {
    if (!slippageTolerance) {
      setMinTokensOut(0)
      return
    }
    handleEstimate()
  }, [slippageTolerance, amount, handleEstimate])

  useEffect(() => {
    if (isInitialRender.current) {
      // Skip the first render
      isInitialRender.current = false
      return
    }

    if (isBuying) {
      buyInputRef.current?.focus()
    } else {
      sellInputRef.current?.focus()
    }
  }, [isBuying])

  const handleAmountChange = useCallback(
    async (e) => {
      try {
        // TODO: validate amount properly - this might be fiddly
        // if (!web3) {
        setMinEthOut(0)
        setMinTokensOut(0)
        const value = e.target.value
        const numericValue = Number(value).toFixed(decimals)
        setAmount(value ? Number(numericValue) : undefined)
        return
      } catch (error) {
        console.error('Error on handleAmountChange', error)
        Sentry.captureException(error)
      }
    },
    [decimals],
  )

  useEffect(() => {
    setIsInEth(isBuying)
  }, [isBuying])

  useEffect(() => {
    setAmount(undefined)
  }, [isInEth])

  const hideTradeInputs = !!liquidityPoolAddress || isSaleComplete

  const handleClickSwitch = useCallback(() => {
    setIsBuying(!isBuying)
    setAmount(undefined)
    if (isBuying) {
      buyInputRef.current?.focus()
    } else {
      sellInputRef.current?.focus()
    }
  }, [isBuying])

  const logoUrl = tokenLogo || '/meme-icon.png'

  const ethPrice = ethPriceInUsd ?? 0
  const tokenPriceInUsd = (tokenPriceInEth ?? 0) * ethPrice

  const formattedTokenOutAmount = (
    (minTokensOut ?? 0) * tokenPriceInUsd
  ).toFixed(2)
  const formattedSellTokensAmount = ((amount ?? 0) * tokenPriceInUsd).toFixed(2)

  const tokenBalanceFromWei = amountFromWei(
    userTokenBalance ?? 0,
    decimals,
  ).toNumber()

  const tokenBalance = Math.floor(
    amountFromWei(userTokenBalance ?? 0, decimals).toNumber(),
  )

  const onClickMax = (amount: number) => (event: React.MouseEvent) => {
    event.stopPropagation()
    setAmount(amount)
  }

  const hasBuyError = isBuying && amount >= Number(ethBalanceInEthFormatted)
  const hasSellError = !isBuying && amount > tokenBalanceFromWei

  const isTradeButtonDisabled = isSubmitting
    || !amount
    || amount <= 0
    || hasBuyError
    || hasSellError

  const formattedTokenBalance = tokenBalance.toLocaleString()

  const formattedEthOut = formatNumberWithDecimalSubscript(
    formatNumberToFirstDecimalDigit(minEthOut),
  )

  const handleClickCard = (value: boolean) => () => {
    setIsBuying(value)
    setAmount(undefined)
  }

  const isGraduated = liquidityPoolAddress || isSaleComplete

  const EthInputComponent = (
    <>
      {isBuying ? (
        <TradeInput
          key={'buy'}
          ref={buyInputRef}
          value={amount}
          type="number"
          onWheel={(e: any) => e?.target?.blur()}
          placeholder={'0'}
          onChange={handleAmountChange}
          disabled={hideTradeInputs || isSubmitting}
          hasError={hasBuyError}
        />
      ) : (
        <H2 s="XL" fw="semibold">
          {formatNumberToFirstDecimalDigit(minEthOut)}
        </H2>
      )}
      <P s="M" c="text-secondary">
        {isBuying
          ? `$${Number((amount ?? 0) * ethPriceInUsd).toFixed(2)}`
          : `$${Number(minEthOut * ethPriceInUsd).toFixed(2)}`}
      </P>
    </>
  )

  const EthPriceAndBalance = (
    <Box dc jfe g={4}>
      <Chip>
        <TokenLogo
          src={'/img/icons/memecoins/base.png'}
          alt="coin logo"
          size={20}
        />
        <Strong s="XL">{'ETH'}</Strong>
      </Chip>
      <Box jfe ac g={4}>
        <P
          s="M"
          c={hasBuyError ? 'function-error' : 'text-secondary'}>
          {ethBalanceInEthFormatted}
        </P>
        {isBuying && (
          <ChipButton
            onClick={onClickMax(Number(ethBalanceInEthFormatted) * 0.90)} // use only 90% of the balance
            bst="alpha"
            bss={isBuying ? 'disabled' : 'enabled'}
          >
            Max
          </ChipButton>
        )}
      </Box>
    </Box>
  )

  const TokenInputComponent = (
    <>
      {!isBuying ? (
        <TradeInput
          key={'sell'}
          ref={sellInputRef}
          value={amount}
          type="number"
          placeholder={'0'}
          onChange={handleAmountChange}
          disabled={hideTradeInputs || isSubmitting}
          hasError={hasSellError}
          onWheel={(e: any) => e?.target?.blur()}
        />
      ) : (
        <H2 s="XL" fw="semibold">
          {showDecimalsIfNeeded(minTokensOut, 0)}
        </H2>
      )}
      <P s="M" c="text-secondary">
        {!isBuying
          ? `$${formattedSellTokensAmount}`
          : `$${formattedTokenOutAmount}`}
      </P>
    </>
  )

  const TokenPriceAndBalance = (
    <Box dc jfe g={4}>
      <Chip>
        <TokenLogo src={logoUrl} alt="coin logo" size={20} />
        <Strong s="XL">{tokenSymbol}</Strong>
      </Chip>
      <Box jfe ac g={4}>
        <P
          s="M"
          c={hasSellError ? 'function-error' : 'text-secondary'}>
          {formattedTokenBalance}
        </P>
        {!isBuying && (
          <ChipButton
            onClick={onClickMax(tokenBalance)}
            bst="alpha"
            bss={isBuying ? 'disabled' : 'enabled'}
          >
            Max
          </ChipButton>
        )}
      </Box>
    </Box>
  )

  return (
    <TradeBox dc g={12}>
      <Box dc g={8} style={{ position: 'relative' }}>
        <InfoContainer
          highlighted={!isGraduated}
          hasError={hasBuyError || hasSellError}
        >
          <Box jc dc g={4}>
            <P s="M" c="text-secondary">
              {isBuying ? 'You Pay' : 'Sell'}
            </P>
            {isBuying ? EthInputComponent : TokenInputComponent}
          </Box>
          {isBuying ? EthPriceAndBalance : TokenPriceAndBalance}
        </InfoContainer>
        <InvertButton onClick={handleClickSwitch}>
          <img src="/img/icons/buttons/switch-vertical.svg" alt="Switch" />
        </InvertButton>
        <InfoContainer jsb>
          <Box jc dc g={4}>
            <P s="M" c="text-secondary">
              Get
            </P>
            {!isBuying ? EthInputComponent : TokenInputComponent}
          </Box>
          {!isBuying ? EthPriceAndBalance : TokenPriceAndBalance}
        </InfoContainer>
      </Box>
      {liquidityPoolAddress || isSaleComplete ? (
        <ThemedAnchor
          $styleType="primary"
          href={`https://www.dextools.io/app/en/base/pair-explorer/${liquidityPoolAddress}`}
          w="100%"
        >
          Trade on DEX
        </ThemedAnchor>
      ) : (
        <>
          {!isLoggedIn ? (
            <ConnectWalletButton w="100%" />
          ) : wrongNetwork ? (
            <SwitchToBaseButton $styleType="primary" w="100%" />
          ) : (
            <ThemedButton
              onClick={handleTrade}
              disabled={isTradeButtonDisabled}
              $styleType={hasBuyError || hasSellError ? 'tertiary' : 'primary'}
              w="100%"
            >
              {isSubmitting
                ? (
                  <AnimatedLoader size={14} />
                )
                : (hasBuyError || hasSellError)
                  ? (
                    <Box jc ac g={8}>
                      <img src="/img/icons/alert-triangle.svg" alt="alert" />
                      <P
                        s="L"
                        c="text-secondary"
                        fw="semibold" style={{ opacity: 0.4 }}
                      >
                        Insufficient {hasBuyError ? 'ETH' : tokenSymbol}
                      </P>
                    </Box>
                  )
                  : amount > 0 ? 'Trade' : 'Enter an amount'
              }
            </ThemedButton>
          )}
        </>
      )}
    </TradeBox>
  )
}

const TradeInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0px;
  background: transparent;
  box-sizing: border-box;
  border: none;
  outline: none;
  font-family: Space Grotesk;
  font-weight: 600;
  ${({ theme }) => {
    const breakpoints = Object.keys(theme.breakpoints).reverse()
    const rules = breakpoints.map(
      (bp) => `
        @media screen and (max-width: ${theme.breakpoints[bp]}px) {
          font-size: ${theme.typography.heading['XL'].size[bp]}px;
          line-height: ${theme.typography.heading['XL'].lineHeight[bp]}px;
        }
      `,
    )

    rules.push(`
      @media screen and (min-width: ${theme.breakpoints.expandedPlus}px) {
        font-size: ${theme.typography.heading['XL'].size.expandedPlus}px;
        line-height: ${theme.typography.heading['XL'].lineHeight.expandedPlus}px;
      } 
    `)
    return rules.join('\n')
  }};
  line-height: 20px;
  position: relative;
  ::placeholder {
    opacity: ${({ theme: { isDark } }) => (isDark ? 0.3 : 0.8)};
  }

  ${({ hasError }) =>
    hasError &&
    css`
      color: ${({ theme }) => theme.semantics.zap.function.error};
    `}
`

export const Button = styled.button`
  border-radius: var(--units-4, 4px);
  background: var(--surface-brand, #ff5740);
  width: 100%;
  color: var(--Midnight, var(--text-invert, #04111b));
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.18px;
  text-transform: uppercase;
  padding: 8px;
  height: 43px;

  :disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TradeBox = styled(Box)`
  // border-radius: var(--units-12, 12px);
  backdrop-filter: blur(12px);
  overflow: hidden;
  .inner {
    background: #161c20;
    padding: 1rem;
  }
  span.min-tokens-out {
    color: var(--white, #f6f8ff);
    text-align: center;

    /* Web/Text/M/Regular */
    -webkit-text-stroke-width: 1;
    -webkit-text-stroke-color: var(--white, #f6f8ff);
    font-family: var(--type-family-space-grotesk, 'Space Grotesk');
    font-size: var(--Text-M-M-size, 12px);
    font-style: normal;
    font-weight: var(--type-weight-Regular, 300);
    line-height: var(--Text-M-M-line-height, 16px); /* 133.333% */
  }
`

const Container = styled(Box)`
  flex-direction: column;
  gap: 24px;
`

export default Invest

export function addFeeToValue(value: bigint): bigint {
  try {
    return value + (value * PCT_FEE) / BigInt(100)
  } catch (error) {
    console.error(`Error adding fee to value: ${value}, PCT_FEE: ${PCT_FEE}`)
    Sentry.captureException(error)
  }
}

export function getWalletTypeFromProvider(key: string) {
  const isOkx = key.includes('okx')
  const isCoinbaseWallet = key.includes('coinbase')
  const isBitKeep = key.includes('bitkeep')
  const isBybit = key.includes('bybit')
  const isRabby = key.includes('rabby')
  const isMetaMask =
    key.includes('metamask') &&
    !isOkx &&
    !isCoinbaseWallet &&
    !isBitKeep &&
    !isBybit &&
    !isRabby

  const walletType = isOkx
    ? 'OKX'
    : isCoinbaseWallet
      ? 'Coinbase'
      : isBitKeep
        ? 'BitKeep'
        : isBybit
          ? 'Bybit'
          : isRabby
            ? 'Rabby'
            : isMetaMask
              ? 'MetaMask'
              : 'Unknown'
  return {
    isOkx,
    isCoinbaseWallet,
    isBitKeep,
    isBybit,
    isRabby,
    isMetaMask,
    walletType,
  }
}

export async function addTokenToWallet(
  tokenAddress: string,
  tokenSymbol: string,
  decimals: number,
  rawWalletProvider: ethers.BrowserProvider,
  primaryWallet: UseDynamicContext['primaryWallet'],
) {
  const hasResetLocalStorageOnce = localStorage.getItem('hasResetLocalStorage')

  if (!hasResetLocalStorageOnce) {
    console.info({ hasResetLocalStorageOnce })
    localStorage.removeItem('allTokensAddedToWallet')
    localStorage.setItem('hasResetLocalStorage', 'true')
  }

  const provider = rawWalletProvider
  const { address: walletAddress, key } = primaryWallet

  const {
    isOkx,
    isCoinbaseWallet,
    isBitKeep,
    isBybit,
    isRabby,
    isMetaMask,
    walletType,
  } = getWalletTypeFromProvider(key)

  if (isBybit) return

  let isOnBaseChain = false

  if (isCoinbaseWallet) {
    const currentChain = await (provider as any).getChainId()
    isOnBaseChain = currentChain === 8453 || currentChain === 84532
  }

  // sorry for the below, it's gross
  if (
    (!!provider && isCoinbaseWallet && !isOnBaseChain) ||
    ((isMetaMask || isRabby || isBitKeep || isBybit) && !isOkx)
  ) {
    let shouldAdd = true
    const allTokensAddedToWallet = localStorage.getItem(
      'allTokensAddedToWallet',
    )

    if (!!allTokensAddedToWallet) {
      const allTokensAddedToWalletParsed = JSON.parse(allTokensAddedToWallet)
      const hasWalletType = !!allTokensAddedToWalletParsed?.[walletType]
      console.info({ hasWalletType, allTokensAddedToWalletParsed, walletType })
      if (hasWalletType) {
        const hasWalletAddress =
          !!allTokensAddedToWalletParsed?.[walletType]?.[walletAddress]
        if (!hasWalletAddress) {
          allTokensAddedToWalletParsed[walletType] = {
            [walletAddress]: [],
          }
        } else {
          shouldAdd =
            !allTokensAddedToWalletParsed?.[walletType]?.[
              walletAddress
            ]?.includes(tokenAddress)
        }
      } else {
        allTokensAddedToWalletParsed[walletType] = {
          [walletAddress]: [],
        }
      }
    }

    if (!shouldAdd) return

    await provider
      .send('wallet_watchAsset', {
        type: 'ERC20',
        options: {
          address: tokenAddress,
          symbol: tokenSymbol,
          decimals,
        },
      })
      .catch((error: any) => {
        console.error('Error adding token to metamask', error)
        if (error?.message === 'User rejected the request.') {
          toast.error('You rejected adding the token to your wallet')
        }
      })

    if (!allTokensAddedToWallet) {
      localStorage.setItem(
        'allTokensAddedToWallet',
        JSON.stringify({ [walletType]: { [walletAddress]: [tokenAddress] } }),
      )
      return
    }

    const allTokensAddedToWalletParsed = JSON.parse(allTokensAddedToWallet)

    if (!allTokensAddedToWalletParsed?.[walletType])
      allTokensAddedToWalletParsed[walletType] = []

    if (!allTokensAddedToWalletParsed?.[walletType]?.[walletAddress])
      allTokensAddedToWalletParsed[walletType][walletAddress] = []

    localStorage.setItem(
      'allTokensAddedToWallet',
      JSON.stringify({
        ...allTokensAddedToWalletParsed,
        [walletType]: {
          ...allTokensAddedToWalletParsed?.[walletType],
          [walletAddress]: [
            ...allTokensAddedToWalletParsed?.[walletType]?.[walletAddress],
            tokenAddress,
          ],
        },
      }),
    )
  }
}

const InfoContainer = styled(Box)<{
  highlighted?: boolean
  hasError?: boolean
}>`
  width: 100%;
  padding: var(--Spacing-Padding-M, 12px);
  align-items: center;
  gap: var(--units-20, 20px);
  border-radius: 8px;
  border: 1px solid var(--stroke-tertiary, #252525);
  cursor: not-allowed;

  ${({ highlighted }) =>
    highlighted &&
    css`
      border: 1px solid var(--stroke-primary, #fff);
      background: linear-gradient(
          0deg,
          var(--states-minimal-alpha-8, rgba(255, 255, 255, 0.08)) 0%,
          var(--states-minimal-alpha-8, rgba(255, 255, 255, 0.08)) 100%
        ),
        var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.75));
      cursor: pointer;
    `}

  ${({ highlighted }) =>
    !highlighted &&
    css`
      :hover {
        border: 1px solid var(--stroke-secondary, #333);
        background: linear-gradient(
            0deg,
            var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 0%,
            var(--states-minimal-alpha-4, rgba(255, 255, 255, 0.04)) 100%
          ),
          var(--surface-invert-alpha-tertiary, rgba(0, 0, 0, 0.75));
      }
    `}

  ${({ hasError }) =>
    hasError &&
    css`
      border: 1px solid var(--colour-function-error, #ff5252);
    `}
`

const InvertButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--surface-primary, #080808);
  border: 1px solid var(--stroke-secondary, #333);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const Chip = styled.div`
  display: flex;
  padding: var(--units-8, 8px) var(--units-12, 12px);
  justify-content: center;
  align-items: center;
  align-self: flex-end;
  gap: var(--units-4, 4px);
  border-radius: 54px;
  background: var(--surface-minimal-alpha-primary, rgba(255, 255, 255, 0.04));
`
